import type { FeatureFlag } from '@alliance-disposal/client';
import { getTodayAsNoonUtc, parseYmdAsNoonUtc } from '@wayste/client-utils';
import routes from '../../../utils/routes';

/** Task group types the unified dispatch view can render. */
export const DispatchTaskTypes = {
    ROLL_OFF: 'ro',
    PORTABLE_TOILET: 'pt',
} as const;

export type DispatchTaskType = (typeof DispatchTaskTypes)[keyof typeof DispatchTaskTypes];

/** Hauler-facing name for each task type — the toggle, the Customize modal's group headings. */
export const DISPATCH_TASK_TYPE_LABELS: Record<DispatchTaskType, string> = {
    [DispatchTaskTypes.ROLL_OFF]: 'Roll-off',
    [DispatchTaskTypes.PORTABLE_TOILET]: 'Portable toilet',
};

export enum DispatchView {
    BOARD = 'board',
    LIST = 'list',
    MAP = 'map',
    CALENDAR = 'calendar',
}

/** How much of the schedule the Calendar view draws at once. Meaningless for the other views. */
export const DispatchSpans = {
    WEEK: 'week',
    DAY: 'day',
} as const;

export type DispatchSpan = (typeof DispatchSpans)[keyof typeof DispatchSpans];

export const DISPATCH_TASK_TYPE_PARAM = 'type';
export const DISPATCH_VIEW_PARAM = 'view';
export const DISPATCH_DATE_PARAM = 'date';
export const DISPATCH_SPAN_PARAM = 'span';

/**
 * The legacy roll-off and portable toilet dispatch pages wrote the board view as `view=schedule`
 * (`DriverTaskViews.SCHEDULE`). The unified view calls it `board`, so old links translate on the way in.
 */
const LEGACY_BOARD_VIEW = 'schedule';

/** `?date=today` is accepted because the portable toilet dispatch page emitted it. */
const TODAY_DATE_VALUE = 'today';

/**
 * Task types the hauler may see, in toggle order.
 *
 * Roll-off needs `wayste-standard` (that flag is exactly `!hauler.waysteLite`) plus
 * `wayste-operations-rolloff`, because the unified view renders the operations roll-off stack rather
 * than the legacy one. Portable toilet needs `wayste-portable-toilet`, matching the guard on the
 * standalone portable toilet routes.
 */
export const getEntitledDispatchTaskTypes = (featureFlags: FeatureFlag[]): DispatchTaskType[] => {
    const entitledTaskTypes: DispatchTaskType[] = [];

    if (featureFlags.includes('wayste-standard') && featureFlags.includes('wayste-operations-rolloff')) {
        entitledTaskTypes.push(DispatchTaskTypes.ROLL_OFF);
    }

    if (featureFlags.includes('wayste-portable-toilet')) {
        entitledTaskTypes.push(DispatchTaskTypes.PORTABLE_TOILET);
    }

    return entitledTaskTypes;
};

/**
 * Resolves `?type=` against what the hauler is entitled to. A missing or unentitled type falls back to
 * the first entitled type rather than 404ing, so a stale bookmark still lands somewhere useful.
 * Null means the hauler is entitled to no task type at all.
 */
export const resolveDispatchTaskType = (rawTaskType: string | null, entitledTaskTypes: DispatchTaskType[]): DispatchTaskType | null => {
    const requestedTaskType = entitledTaskTypes.find((taskType) => taskType === rawTaskType);
    return requestedTaskType ?? entitledTaskTypes[0] ?? null;
};

/** Mobile defaults to list — neither the board nor the calendar is usable there. */
const defaultDispatchView = (isDesktop: boolean): DispatchView => (isDesktop ? DispatchView.BOARD : DispatchView.LIST);

/**
 * Resolves `?view=`, translating the legacy `schedule` value.
 *
 * `isCalendarAvailable` is the rollout flag and task type together — a hauler without the flag, or on a
 * task type the calendar can't draw, falls back rather than landing on a view that would render nothing.
 * Desktop is a further requirement: a seven-column grid has nowhere to go on a phone.
 */
export const resolveDispatchView = (rawView: string | null, isDesktop: boolean, isCalendarAvailable = false): DispatchView => {
    switch (rawView?.toLowerCase()) {
        case DispatchView.LIST: {
            return DispatchView.LIST;
        }
        case DispatchView.MAP: {
            return DispatchView.MAP;
        }
        // A legacy `?view=calendar` link lands on the schedule. The week grid is what `?span=week` draws
        // there, so the link still resolves to what it always meant.
        case DispatchView.CALENDAR: {
            return defaultDispatchView(isDesktop);
        }
        case DispatchView.BOARD:
        case LEGACY_BOARD_VIEW: {
            return DispatchView.BOARD;
        }
        default: {
            return defaultDispatchView(isDesktop);
        }
    }
};

/**
 * Resolves `?span=`. Day unless the URL explicitly asks for a week.
 *
 * Forced to day where the week grid cannot be drawn — a hauler outside the rollout, a task type the
 * calendar does not know, or a screen too narrow for seven columns. Otherwise the control would offer a
 * week the schedule then renders as a day, which reads as the control being broken.
 */
export const resolveDispatchSpan = (rawSpan: string | null, isCalendarAvailable = true): DispatchSpan => {
    if (!isCalendarAvailable) return DispatchSpans.DAY;

    return rawSpan?.toLowerCase() === DispatchSpans.WEEK ? DispatchSpans.WEEK : DispatchSpans.DAY;
};

/**
 * Resolves `?date=`. Anchored at noon UTC rather than local midnight so the visible day is the same
 * calendar day regardless of the browser's timezone relative to the hauler's — including when the param
 * is absent.
 *
 * Local midnight is not interchangeable with the noon-UTC anchor: an adapter that re-reads the date in
 * the *hauler's* timezone — roll-off's provider does, with `startOfDayInTimezone` — can land on the
 * previous day when the browser sits east of the hauler. Going through the same `yyyy-MM-dd` round trip
 * makes a bare `/dispatch` behave exactly like `/dispatch?date=<today>`.
 */
export const resolveDispatchDate = (rawDate: string | null, haulerTimeZone: string): Date => {
    if (!rawDate || rawDate === TODAY_DATE_VALUE) return getTodayAsNoonUtc(haulerTimeZone);

    return parseYmdAsNoonUtc(rawDate) ?? getTodayAsNoonUtc(haulerTimeZone);
};

export type ResolvedDispatchParams = {
    entitledTaskTypes: DispatchTaskType[];
    taskType: DispatchTaskType | null;
    view: DispatchView;
    date: Date;
    span: DispatchSpan;
    /** Whether the Calendar toggle should be offered at all — the flag, the task type and desktop together. */
    isCalendarAvailable: boolean;
};

/**
 * Takes the entitled types rather than the raw feature flags: the page gets them from
 * `useUnifiedDispatchTaskTypes`, which is also what gates the route and the nav links. Deriving them a
 * second time here would let the toggle offer a type the guard doesn't serve.
 */
export const resolveDispatchParams = ({
    searchParams,
    entitledTaskTypes,
    isDesktop,
    isCalendarEnabled = false,
    haulerTimeZone,
}: {
    searchParams: URLSearchParams;
    entitledTaskTypes: DispatchTaskType[];
    isDesktop: boolean;
    /** The rollout flag alone. Availability also depends on the resolved task type, decided here. */
    isCalendarEnabled?: boolean;
    haulerTimeZone: string;
}): ResolvedDispatchParams => {
    const taskType = resolveDispatchTaskType(searchParams.get(DISPATCH_TASK_TYPE_PARAM), entitledTaskTypes);
    // Derived from the *resolved* type, not the raw param: an unentitled `?type=pt` falls back to roll-off,
    // and the calendar should be offered for what the hauler is actually looking at.
    const isCalendarAvailable = isCalendarEnabled && isDesktop && taskType === DispatchTaskTypes.ROLL_OFF;

    return {
        entitledTaskTypes,
        taskType,
        isCalendarAvailable,
        view: resolveDispatchView(searchParams.get(DISPATCH_VIEW_PARAM), isDesktop, isCalendarAvailable),
        date: resolveDispatchDate(searchParams.get(DISPATCH_DATE_PARAM), haulerTimeZone),
        span: resolveDispatchSpan(searchParams.get(DISPATCH_SPAN_PARAM), isCalendarAvailable),
    };
};

/**
 * Search string for redirecting a legacy dispatch URL to `/dispatch`. Every existing param is carried
 * over — `date`, `task`, `viewAll`, `accordion` and anything else a deep link happens to hold — with
 * `view` translated and `type` set.
 */
export const buildUnifiedDispatchSearch = (search: string, taskType: DispatchTaskType): string => {
    const params = new URLSearchParams(search);

    if (params.get(DISPATCH_VIEW_PARAM)?.toLowerCase() === LEGACY_BOARD_VIEW) {
        params.set(DISPATCH_VIEW_PARAM, DispatchView.BOARD);
    }

    params.set(DISPATCH_TASK_TYPE_PARAM, taskType);

    return `?${params.toString()}`;
};

/** Path to the unified dispatch view for a given task type, for nav entries and links. */
export const buildDispatchPath = (taskType: DispatchTaskType): string => `${routes.dispatch.list}?${DISPATCH_TASK_TYPE_PARAM}=${taskType}`;
